"use client";

import { useEffect, useState, use } from "react";
import { apiFetch } from "@/lib/api-fetch";

import { useTranslations } from "next-intl";
import Link from "next/link";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { Loader2, Wand2 } from "lucide-react";
import { EpisodeBreadcrumb } from "@/components/editor/episode-breadcrumb";
import { ProjectSidebar } from "@/components/editor/project-sidebar";

/**
 * 顶栏里的「当前阶段」文案（第 4 级面包屑）。
 *
 * 只列**分集级**路由段：`/episodes/{episodeId}/{stage}` 的第三段。
 * 项目级页面（`/episodes`、`/characters`、`/assets`…）没有这一级，面包屑只到项目为止。
 */
const STAGE_LABEL: Record<string, { ns: "project" | "prop"; key: string }> = {
  script: { ns: "project", key: "script" },
  characters: { ns: "project", key: "characters" },
  scenes: { ns: "project", key: "scenes" },
  props: { ns: "prop", key: "title" },
  storyboard: { ns: "project", key: "storyboard" },
  preview: { ns: "project", key: "preview" },
};

/**
 * 顶栏只需要项目标题。
 *
 * 🔴 2026-09-17「菜单跳转非常慢」根治：本布局挂在**所有**项目子路由上
 * （含 `/episodes/{episodeId}/...` 全部页面），但改造前它调
 * `fetchProject(id)` → 拉整个项目的 120 个镜头 + 6 集剧本 = **386 KB**，
 * 只为渲染一个 `title`。紧接着剧集层 layout 又拉 354 KB，
 * 于是**每次点菜单固定消耗 741 KB**。
 *
 * 现在走 `?lite=1`：只取项目自身字段，~2 KB。剧集详情页的数据由
 * 剧集层 layout 的 `fetchProject(id, episodeId)` 正常提供，互不影响。
 *
 * 用本地 state 而非全局 store：标题是这份布局的私有数据，
 * 塞进 store 会与「当前剧集数据」的语义打架（两者 id 相同、内容不同）。
 */
function useProjectTitle(id: string) {
  const [title, setTitle] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setTitle(null);
    setFailed(false);
    (async () => {
      try {
        const res = await apiFetch(`/api/projects/${id}?lite=1`);
        const data = (await res.json()) as { title?: string };
        if (alive) setTitle(data.title ?? "");
      } catch {
        // 标题取不到不该把整页卡死 —— 降级为空标题，内容区照常渲染。
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  return { title, loading: title === null && !failed };
}

export default function ProjectLayout({
  children,
  params,
}: {
  children: React.ReactNode;
  params: Promise<{ id: string }>;
}) {
  const { id } = use(params);
  const t = useTranslations("common");
  const tProject = useTranslations("project");
  const tProp = useTranslations("prop");
  const locale = useLocale();
  const pathname = usePathname();
  const { title, loading } = useProjectTitle(id);

  /**
   * 从路径里取「当前集 / 当前阶段」给顶栏面包屑用。
   * 用 `lastIndexOf("episodes")` 而不是固定下标：本应用挂在 basePath
   * （`/plugins/.../aicomic/zh/...`）下，`usePathname()` 的前缀段数不固定，
   * 按段名定位才不会在带前缀/不带前缀两种情况下错位。
   */
  const segs = pathname.split("/").filter(Boolean);
  const epIdx = segs.lastIndexOf("episodes");
  const episodeId = epIdx >= 0 && segs.length > epIdx + 1 ? segs[epIdx + 1] : null;
  const stageKey = episodeId && segs.length > epIdx + 2 ? segs[epIdx + 2] : null;
  const stageMeta = stageKey ? STAGE_LABEL[stageKey] : undefined;
  const stageLabel = stageMeta
    ? (stageMeta.ns === "prop" ? tProp(stageMeta.key) : tProject(stageMeta.key))
    : undefined;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          {/* 🔴 2026-10-11「hydration mismatch：attributes of the server rendered
              HTML didn't match」定位：本项目代码 **零处** 产生 `data-imt-*`。
              这两个属性来自浏览器扩展「沉浸式翻译」（Immersive Translate）——
              它在 document_start 注入 content script，用 MutationObserver 给
              文本节点打标记（`data-imt-p` = 已处理段落，
              `data-imt_insert_failed_reason="same_text"` = 译文与原文相同、无需插入）。
              它在 **SSR HTML 落地之后、React hydrate 之前** 改写了这个 <p>，
              于是 React 比对时发现多出两个属性 → 报 mismatch。
              本布局中只有这个 loading 骨架是 SSR 文本（顶栏标题走 ?lite=1
              客户端拉取，SSR 时同样处于 loading 态），所以唯一的告警点就在这里。
              suppressHydrationWarning 只对**本元素自身**生效（不向下继承），
              用于压掉这条纯外部造成的噪音，属于项目既有逃生舱
              （见 [locale]/layout.tsx 的 <html>/<body>）。 */}
          <p
            className="text-sm text-[var(--text-muted)]"
            suppressHydrationWarning
          >
            {t("loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top bar */}
      <header className="relative z-20 flex h-14 flex-shrink-0 items-center justify-between border-b border-[var(--border-subtle)] bg-white/80 backdrop-blur-xl px-4 lg:px-6">
        {/* 🔴 2026-10-21（用户：这里要显示「哪个项目的哪一集」，并支持
            全部项目 → 项目 → 集 → 镜头 的层级跳转）：
            改造前这里只有「← + 项目名」，集名只存在于 URL —— 从深链进来、
            或同时开几集对比时认不出自己在哪一集。
            现在整块换成层级面包屑，第 1 级「全部项目」同时承担原来那个返回箭头的作用。
            `min-w-0` 链一路带下去，长集名才肯 truncate 而不是把顶栏撑破。 */}
        <div className="flex min-w-0 flex-1 items-center">
          <EpisodeBreadcrumb
            projectId={id}
            projectTitle={title ?? ""}
            episodeId={episodeId}
            stage={stageKey ?? "script"}
            stageLabel={stageLabel}
          />
        </div>
        <div className="flex items-center gap-1">
<Link
            href={`/${locale}/settings/prompts?scope=project&projectId=${id}`}
            title="项目提示词"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-muted)] transition-all hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
          >
            <Wand2 className="h-4 w-4" />
          </Link>
          {/* 设置与语言切换已上移到全局顶栏 GlobalHeader（[locale]/layout.tsx），
              这里保留的 Wand2 是**项目级**提示词入口（带 scope/projectId 上下文），
              与全局的通用入口语义不同，不能删。 */}
        </div>
      </header>

      {/* Content：项目级左侧栏（所有项目页共享）+ 内容区。
          侧栏挂在这一层而不是剧集详情布局里 —— 否则从详情页返回「分集管理」时
          左侧栏会整个消失（本文件原先只有顶栏，而 loading.tsx 的骨架却是「侧栏 + 内容区」）。 */}
      <div className="flex flex-1 items-stretch">
        <ProjectSidebar projectId={id} />
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
}
